// Lightweight day grouping for the chat thread. Pure date math, no time-zone
// trickery — uses the local browser timezone (which is what `Date` already
// gives us when parsing an ISO string).

import {
    differenceInCalendarDays,
    format,
    isToday,
    isYesterday,
    parseISO,
} from "date-fns";

/**
 * Returns a stable, locale-friendly day label for a chat group:
 *   "Today" | "Yesterday" | "Mon, Apr 28" | "Apr 28, 2024"
 *
 * Uses `Mon, Apr 28` for messages from this calendar year, and falls back to
 * the year-qualified form for older threads. Keeps the divider compact.
 */
export function dayLabelFor(iso) {
    const d = typeof iso === "string" ? parseISO(iso) : new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    if (isToday(d)) return "Today";
    if (isYesterday(d)) return "Yesterday";
    const now = new Date();
    if (d.getFullYear() === now.getFullYear()) {
        return format(d, "EEE, MMM d");
    }
    return format(d, "MMM d, yyyy");
}

/**
 * Stable per-day key — used to decide when to insert a divider between
 * consecutive messages.
 */
export function dayKeyFor(iso) {
    const d = typeof iso === "string" ? parseISO(iso) : new Date(iso);
    if (Number.isNaN(d.getTime())) return "invalid";
    return format(d, "yyyy-MM-dd");
}

export function daysAgo(iso) {
    const d = typeof iso === "string" ? parseISO(iso) : new Date(iso);
    if (Number.isNaN(d.getTime())) return 0;
    return differenceInCalendarDays(new Date(), d);
}
